import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, get, child } from 'firebase/database';
import firebaseConfig from '../../config/firebase-config.js';
import useAuthStore from '../hooks/useAuthStore.js';

// Initialize Firebase app at module level (singleton pattern)
let app = null;
let db = null;

function getFirebaseDatabase() {
  if (!app) {
    app = initializeApp(firebaseConfig);
  }
  if (!db) {
    db = getDatabase(app);
  }
  return db;
}

export default function ResourceCategory() {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState('textbooks');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBook, setSelectedBook] = useState(null);
  const [books, setBooks] = useState([]);

  // Get Firebase database instance
  const db = getFirebaseDatabase();

  // Determine user's curriculum and grade from stream
  const userStream = user?.stream || 'ICSE Class 10'; // Default to Class 10 if not set
  const isISC = userStream.includes('ISC');
  const isClass12 = userStream.includes('Class 12');
  const isClass11 = userStream.includes('Class 11');
  const isClass9 = userStream.includes('Class 9');
  const gradeLevel = isClass12 ? 'Class 12' : isClass11 ? 'Class 11' : isClass9 ? 'Class 9' : 'Class 10';

  // Dynamic category configuration based on user's curriculum
  const categoryInfo = {
    chemistry: { 
      title: `${gradeLevel} Chemistry`, 
      icon: '🧪', 
      color: 'from-green-500 to-emerald-500',
      filterCategories: isClass12 && isISC 
        ? ['Class 12 Chemistry', 'Chemistry Workbook', 'ISC Chemistry']
        : isClass11
        ? ['Class 11 Chemistry', 'Chemistry Workbook', 'ISC Chemistry']
        : isClass9
        ? ['Class 9 Chemistry', 'Chemistry Workbook']
        : ['Class 10 Chemistry', 'Chemistry Workbook']
    },
    physics: { 
      title: `${gradeLevel} Physics`, 
      icon: '⚛️', 
      color: 'from-blue-500 to-cyan-500',
      filterCategories: isClass12 && isISC
        ? ['Class 12 Physics', 'Physics Workbook', 'ISC Physics']
        : isClass11
        ? ['Class 11 Physics', 'Physics Workbook', 'ISC Physics']
        : isClass9
        ? ['Class 9 Physics', 'Physics Workbook']
        : ['Class 10 Physics', 'Physics Workbook']
    },
    biology: { 
      title: `${gradeLevel} Biology`, 
      icon: '🧬', 
      color: 'from-pink-500 to-rose-500',
      filterCategories: isClass12 && isISC
        ? ['Class 12 Biology', 'Biology Workbook', 'ISC Biology']
        : isClass11
        ? ['Class 11 Biology', 'Biology Workbook', 'ISC Biology']
        : isClass9
        ? ['Class 9 Biology', 'Biology Workbook']
        : ['Class 10 Biology', 'Biology Workbook']
    },
    english: { 
      title: `${gradeLevel} English`, 
      icon: '📖', 
      color: 'from-purple-500 to-indigo-500',
      filterCategories: isClass12 && isISC
        ? ['Class 12 English', 'English Workbook', 'English Literature Workbook', 'English Grammar', 'ISC English']
        : isClass11
        ? ['Class 11 English', 'English Workbook', 'English Literature Workbook', 'English Grammar', 'ISC English']
        : isClass9
        ? ['Class 9 English', 'English Workbook', 'English Literature Workbook', 'English Grammar']
        : ['Class 10 English', 'English Workbook', 'English Literature Workbook', 'English Grammar']
    }
  };

  const currentCategory = categoryInfo[categoryId] || categoryInfo.chemistry;

  useEffect(() => {
    async function fetchBooks() {
      try {
        console.log('Fetching books from Firebase Realtime Database...');
        const dbRef = ref(db);
        const snapshot = await get(child(dbRef, 'textbooks'));
        
        console.log('Firebase snapshot exists:', snapshot.exists());
        
        if (snapshot.exists()) {
          const booksData = Object.values(snapshot.val());
          console.log('Fetched books count:', booksData.length);
          console.log('Sample book:', booksData[0]);
          setBooks(booksData);
        } else {
          console.log('No books found in Firebase Realtime Database');
          setBooks([]);
        }
      } catch (error) {
        console.error('Error fetching books:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        console.error('Error details:', error);
        setBooks([]);
      }
    }

    fetchBooks();
  }, [user]);

  const filteredBooks = books.filter(book => {
    const currentFilterCategories = currentCategory.filterCategories || [currentCategory.title];
    const matchesCategory = currentFilterCategories.some(cat => 
      book.category.toLowerCase().includes(cat.toLowerCase())
    ) || (categoryId === 'english' && book.subject === 'English');
    const matchesSearch = book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         book.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         book.publisher.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'textbooks' 
      ? !book.category.toLowerCase().includes('workbook')
      : book.category.toLowerCase().includes('workbook');
    return matchesCategory && matchesSearch && matchesTab;
  });

  const handleBookClick = (book) => {
    setSelectedBook(book);
  };

  const handleCloseModal = () => {
    setSelectedBook(null);
  };

  const renderStarRating = (rating, bookId) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center gap-1">
        {Array.from({ length: fullStars }).map((_, i) => (
          <svg key={`full-star-${bookId}-${i}`} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        {hasHalfStar && (
          <svg key={`half-star-${bookId}`} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        )}
        {Array.from({ length: emptyStars }).map((_, i) => (
          <svg key={`empty-star-${bookId}-${i}`} className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        <span className="text-gray-400 text-sm ml-2">{rating}</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen w-full text-white antialiased bg-[linear-gradient(180deg,_#0d0b18_0%,_#16122c_50%,_#0b0914_100%)] relative overflow-x-hidden">
      {/* Soft, silk-like velvet glow overlay */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[120px] pointer-events-none" />

      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/resources')}
              className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">{currentCategory.title}</h1>
              <p className="text-purple-400 text-sm">{currentCategory.icon} {activeTab === 'textbooks' ? 'Textbooks' : 'Workbooks'}</p>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search textbooks by title, subject, or publisher..."
                className="w-full px-6 py-4 bg-white/10 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all"
              />
              <svg className="absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </motion.div>

          {/* Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-6"
          >
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => setActiveTab('textbooks')}
                className={`px-8 py-3 rounded-xl font-medium transition-all duration-300 ${
                  activeTab === 'textbooks'
                    ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg shadow-purple-500/25'
                    : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white'
                }`}
              >
                Textbooks
              </button>
              <button
                onClick={() => setActiveTab('workbooks')}
                className={`px-8 py-3 rounded-xl font-medium transition-all duration-300 ${
                  activeTab === 'workbooks'
                    ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg shadow-purple-500/25'
                    : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white'
                }`}
              >
                Workbooks
              </button>
            </div>
          </motion.div>

          {/* Book Listings - E-commerce Style with Virtualization */}
          <motion.div
            key={activeTab + searchQuery}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {filteredBooks.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <div className="text-6xl mb-4">📚</div>
                <p className="text-gray-400 text-lg">No textbooks found matching your search.</p>
                <p className="text-gray-500 text-sm mt-2">Try adjusting your search or switch tabs.</p>
              </motion.div>
            ) : (
              <div className="space-y-4">
                {filteredBooks.map((book) => (
                  <motion.div
                    key={book.id || book.title}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0 }}
                    onClick={() => handleBookClick(book)}
                    className="glass-card rounded-xl p-6 cursor-pointer hover:scale-[1.01] transition-all duration-300 border border-white/10 hover:border-purple-500/30"
                  >
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Book Cover - Left Side */}
                      <div className="w-full md:w-40 h-56 bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {book.imageUrl ? (
                          <img src={book.imageUrl} alt={book.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="text-7xl">📚</div>
                        )}
                      </div>

                      {/* Book Details - Right Side */}
                      <div className="flex-1 flex flex-col justify-between">
                        <div>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="text-white font-bold text-lg mb-1">{book.title}</h3>
                              <p className="text-gray-400 text-sm mb-2">{book.author} • {book.publisher}</p>
                            </div>
                            {renderStarRating(book.rating, book.id || book.title)}
                          </div>
                          <p className="text-gray-300 text-sm mb-3 line-clamp-2">{book.description}</p>
                        </div>
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                          <span className="text-xs text-gray-500">{book.category}</span>
                          <div className="flex items-center gap-2 text-purple-400 text-sm">
                            <span>View Details</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Amazon Associates Disclaimer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-12 text-center"
          >
            <p className="text-gray-500 text-xs">
              As an Amazon Associate, this site earns from qualifying purchases.
            </p>
          </motion.div>
        </div>
      </main>

      {/* Detailed Book Modal */}
      <AnimatePresence>
        {selectedBook && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="glass-card rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/10"
            >
              {/* Modal Header */}
              <div className="p-6 border-b border-white/10 flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-white mb-2">{selectedBook.title}</h2>
                  <p className="text-purple-400 text-sm mb-1">{selectedBook.subtitle}</p>
                  <p className="text-gray-400 text-sm">
                    {selectedBook.author} • {selectedBook.publisher}
                  </p>
                  {renderStarRating(selectedBook.rating, selectedBook.id || selectedBook.title)}
                </div>
                <button
                  onClick={handleCloseModal}
                  className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Book Cover */}
                <div className="w-full h-64 bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-xl flex items-center justify-center overflow-hidden">
                  {selectedBook.imageUrl ? (
                    <img src={selectedBook.imageUrl} alt={selectedBook.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-8xl">📚</div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <h3 className="text-white font-semibold mb-2">Description</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{selectedBook.description}</p>
                </div>

                {/* Key Highlights */}
                <div>
                  <h3 className="text-white font-semibold mb-3">Key Highlights</h3>
                  <ul className="space-y-2">
                    {selectedBook.highlights.map((highlight) => (
                      <li key={highlight} className="flex items-start gap-2 text-gray-300 text-sm">
                        <span className="text-purple-400 mt-0.5">✓</span>
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Amazon CTA Button */}
                <a
                  href={selectedBook.amazonAffiliateUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full py-4 bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] text-center"
                >
                  Check Price on Amazon
                </a>

                {/* Amazon Associates Disclaimer */}
                <p className="text-gray-500 text-xs text-center">
                  As an Amazon Associate, this site earns from qualifying purchases.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
